/**
 * EvidenceViewDialog — read-only view of a milestone's saved AI artefact.
 *
 * Opens from the "View artefact" button on cleared milestones that have
 * AI-saved evidence. Shows the structured draft + provenance (who saved,
 * when, the original business brief that produced it).
 *
 * Backend: GET /api/scaling-lean/roadmap/{startup_id}/evidence/{milestone_id}
 */
import React, { useEffect, useState } from 'react';
import { X, Loader2, Award, FileText, Copy, Check, Wand2, ClipboardList, History, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the milestone-open effect. */

const HUMANIZE = (k) =>
  k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function EvidenceViewDialog({ open, onClose, startupId, milestone, onRegenerate, onRestored }) {
  const [loading, setLoading] = useState(false);
  const [restoringIdx, setRestoringIdx] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [history, setHistory] = useState([]);
  const [copiedKey, setCopiedKey] = useState(null);

  const refresh = () => {
    if (!startupId || !milestone) return;
    setLoading(true);
    api
      .get(`/scaling-lean/roadmap/${startupId}/evidence/${milestone.id}`)
      .then(({ data }) => {
        setEvidence(data.evidence);
        setHistory(data.history || []);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load evidence.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !milestone) return;
    setEvidence(null);
    setHistory([]);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, milestone, startupId]);

  if (!open || !milestone) return null;

  const copyField = async (key, value) => {
    try {
      const text = Array.isArray(value) ? value.join('\n') : String(value ?? '');
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast.error('Could not copy.');
    }
  };

  /**
   * Restore a prior draft from history. The backend swaps it with the
   * current evidence (which gets archived back into history) so this
   * is a non-destructive flip — the user can roll back and forth.
   */
  const restore = async (idx) => {
    setRestoringIdx(idx);
    try {
      const { data } = await api.post(
        `/scaling-lean/roadmap/${startupId}/evidence/${milestone.id}/restore`,
        { history_index: idx },
      );
      setEvidence(data.evidence);
      setHistory(data.history || []);
      toast.success('Restored prior draft.');
      if (typeof onRestored === 'function') onRestored(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not restore.');
    } finally {
      setRestoringIdx(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="evidence-view-dialog"
    >
      <div
        className="relative max-w-3xl w-full max-h-[92vh] overflow-y-auto rounded-2xl bg-white dark:bg-[#0d0d0d] shadow-2xl border border-[#8B3FC5]/40"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 dark:bg-[#0d0d0d]/95 backdrop-blur-sm border-b border-slate-200 dark:border-white/10 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] dark:text-[#D4AF37]">
              <Award size={11} /> Artefact of record · OOH™
            </div>
            <h2 className="display text-xl sm:text-2xl font-bold mt-1.5 leading-tight">
              {evidence?.tool_label || milestone.label}
            </h2>
            <p className="text-xs text-slate-500 dark:text-white/55 mt-1">
              For milestone: <span className="font-bold text-slate-700 dark:text-white/80">{milestone.label}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="evidence-view-close"
            className="h-9 w-9 rounded-full bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 flex items-center justify-center shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {loading && (
            <div className="flex items-center justify-center py-12 text-sm text-slate-500 gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading artefact…
            </div>
          )}

          {!loading && !evidence && (
            <div className="text-center py-12">
              <ClipboardList size={28} className="mx-auto text-slate-300 dark:text-white/20" />
              <p className="mt-3 text-sm text-slate-500 dark:text-white/55">
                No AI artefact saved for this milestone yet.
              </p>
              {onRegenerate && (
                <button
                  type="button"
                  onClick={onRegenerate}
                  data-testid="evidence-view-create-new"
                  className="ve-btn-primary mt-4 inline-flex items-center gap-1.5 text-sm"
                >
                  <Wand2 size={13} /> Draft one with AI
                </button>
              )}
            </div>
          )}

          {!loading && evidence && (
            <div data-testid="evidence-view-content">
              {/* Provenance */}
              <div className="rounded-lg border border-slate-200 dark:border-white/10 bg-slate-50/60 dark:bg-white/5 p-4 mb-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">
                  Provenance
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <dt className="text-slate-500 dark:text-white/55">Source</dt>
                  <dd className="font-bold">AI-assisted draft</dd>
                  <dt className="text-slate-500 dark:text-white/55">Saved</dt>
                  <dd className="font-bold">{new Date(evidence.generated_at).toLocaleString()}</dd>
                  {evidence.cleared_at && (
                    <>
                      <dt className="text-slate-500 dark:text-white/55">Cleared</dt>
                      <dd className="font-bold text-[#5E1F8B] dark:text-[#8B3FC5]">
                        {new Date(evidence.cleared_at).toLocaleString()}
                      </dd>
                    </>
                  )}
                  <dt className="text-slate-500 dark:text-white/55">Signed by</dt>
                  <dd className="font-mono text-[11px]">{evidence.generated_by || '—'}</dd>
                  {history.length > 0 && (
                    <>
                      <dt className="text-slate-500 dark:text-white/55">Prior drafts</dt>
                      <dd className="font-bold">{history.length}</dd>
                    </>
                  )}
                </dl>
                {evidence.business_brief && (
                  <details className="mt-3 text-xs">
                    <summary className="cursor-pointer text-slate-500 dark:text-white/55 font-bold uppercase tracking-[0.22em]">
                      Original brief
                    </summary>
                    <p className="mt-2 whitespace-pre-line text-slate-600 dark:text-white/65">
                      {evidence.business_brief}
                    </p>
                  </details>
                )}
              </div>

              {/* Draft fields */}
              <div className="space-y-3">
                {Object.entries(evidence.draft || {}).map(([k, v]) => {
                  const human = HUMANIZE(k);
                  const isArr = Array.isArray(v);
                  const display = isArr
                    ? v
                    : (typeof v === 'string' ? v : JSON.stringify(v, null, 2));
                  return (
                    <div
                      key={k}
                      className="rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1a1a1a] p-4"
                      data-testid={`evidence-field-${k}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">
                          {human}
                        </span>
                        <button
                          onClick={() => copyField(k, v)}
                          className="text-[10px] inline-flex items-center gap-1 text-slate-500 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] font-bold"
                        >
                          {copiedKey === k ? <><Check size={10} /> Copied</> : <><Copy size={10} /> Copy</>}
                        </button>
                      </div>
                      {isArr ? (
                        <ul className="space-y-1">
                          {display.map((item, i) => (
                            <li key={`${k}-${i}`} className="text-sm text-slate-700 dark:text-white/80 flex gap-2">
                              <span className="text-[#8B3FC5] font-bold">·</span>
                              <span>{String(item)}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-slate-700 dark:text-white/80 whitespace-pre-line">{display}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* History of prior drafts — each with restore button */}
              {history.length > 0 && (
                <div className="mt-6 border-t border-slate-200 dark:border-white/10 pt-4" data-testid="evidence-view-history">
                  <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">
                    <History size={11} /> Prior drafts · {history.length}
                  </div>
                  <ol className="space-y-2">
                    {history.map((h, idx) => {
                      const when = h.generated_at
                        ? new Date(h.generated_at).toLocaleString()
                        : '—';
                      const firstKey = Object.keys(h.draft || {})[0];
                      const firstVal = firstKey ? h.draft[firstKey] : null;
                      const raw = Array.isArray(firstVal) ? firstVal.join(' · ') : String(firstVal ?? '');
                      const preview = raw.length > 120 ? raw.slice(0, 117) + '…' : raw;
                      return (
                        <li
                          key={`hist-${idx}`}
                          className="rounded-lg border border-slate-200 dark:border-white/10 bg-slate-50/60 dark:bg-white/5 p-3 flex items-start justify-between gap-3"
                          data-testid={`evidence-history-item-${idx}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-bold text-slate-700 dark:text-white/80">
                              {h.tool_label || 'AI draft'}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/40 font-bold mt-0.5">
                              {when}
                            </div>
                            {preview && (
                              <p className="mt-1 text-[11px] text-slate-500 dark:text-white/55 italic line-clamp-2">
                                &ldquo;{preview}&rdquo;
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => restore(idx)}
                            disabled={restoringIdx !== null}
                            data-testid={`evidence-history-restore-${idx}`}
                            title="Make this the current artefact (archives the current one to history)"
                            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-white/15 bg-white dark:bg-white/5 hover:border-[#8B3FC5] text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 dark:text-white/70 disabled:opacity-50 transition"
                          >
                            {restoringIdx === idx
                              ? <Loader2 size={11} className="animate-spin" />
                              : <Undo2 size={11} />}
                            Restore
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}

              {onRegenerate && (
                <div className="mt-5 border-t border-slate-200 dark:border-white/10 pt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={onRegenerate}
                    data-testid="evidence-view-regenerate"
                    className="text-xs font-bold text-[#5E1F8B] dark:text-[#D4AF37] hover:underline inline-flex items-center gap-1"
                  >
                    <Wand2 size={11} /> Generate a fresh draft (archives this one)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
